import { ApiErrorResponse, ApiOkResponse, ApiResponse, ApisauceInstance } from 'apisauce';
import { AxiosRequestConfig } from 'axios';
import LangEnum from '../lang/LangEnum';

const isNew = (id: string) => /new/.test(id);

export type TeceoApiResponse<T> =
  | ApiOkResponse<T>
  | { ok: false; data: ErrorResponse }
  | ApiErrorResponse<ErrorResponse>;

export interface BasicRepository<T = any, K = any> {
  create: (data: T, options?: K, axiosConfig?: AxiosRequestConfig) => Promise<TeceoApiResponse<T>>;
  update: (data: T, options?: K, axiosConfig?: AxiosRequestConfig) => Promise<TeceoApiResponse<T>>;
  getOne: () => Promise<TeceoApiResponse<T>>;
  remove: () => Promise<TeceoApiResponse<T>>;
}

type BasicRepositoryProps = {
  path: string;
  id?: string;
  restApi: ApisauceInstance;
};

export type ErrorResponse = { status?: number; code?: LangEnum; message?: string; data?: any };

export const handlerApiError = <T = any>(
  response: ApiResponse<T, ErrorResponse>,
): TeceoApiResponse<T> => {
  if (response.ok) {
    return response;
  }
  switch (true) {
    case response.status === 409:
      return {
        ok: false,
        data: {
          code: response.data?.code,
          message: response?.data?.message || 'Campo já foi cadastrado',
          status: response.status,
          data: response.data?.data || null,
        },
      };
    case response.problem === 'NETWORK_ERROR':
      return {
        ok: false,
        data: {
          code: response.data?.code,
          message: 'Servidor não encontrado. Problema na conexão',
          status: response.status,
        },
      };
    default:
      return response;
  }
};

const basicRepository = ({ path, id, restApi }: BasicRepositoryProps): BasicRepository => {
  if (!restApi) {
    throw Error('faltou passar restApi para o repository');
  }
  if (!path) {
    throw Error('faltou passar path para o repository');
  }

  const buildPath = () => {
    if (id && !isNew(id)) {
      return `${path}/${id}`;
    }
    return path;
  };

  const create = async (data: any, options?: AxiosRequestConfig) =>
    handlerApiError(await restApi.post(buildPath(), data, options));

  const update = async (data: any, options?: AxiosRequestConfig) =>
    handlerApiError(await restApi.patch(`${buildPath()}`, data, options || {}));

  const getOne = async () => handlerApiError(await restApi.get(buildPath()));

  const remove = async () => handlerApiError(await restApi.delete(buildPath()));

  return {
    create,
    update,
    getOne,
    remove,
  };
};

export default basicRepository;
